import React, {Fragment} from 'react'
import useStateObject from 'admin/hooks/useStateObject'
import useAdmin from 'admin/hooks/useAdmin'
import useEffect from 'admin/hooks/useEffect'
import useBoolState from 'admin/hooks/useBoolState'
import InputField from 'admin/components/shared/InputField'
import Confirmation from 'admin/components/Confirmation'
import Modal from 'admin/components/shared/Modal'
import {isEmpty, omitBy} from 'lodash'
import {diff} from 'deep-diff'
import Header from '../Header'

export default ({email = {}, errors = {}, onSend, onSave, buttonText, onExit, children, placeholders = {}}) => {
  const {admin} = useAdmin()
  const [form, setForm, resetForm] = useStateObject(email)
  const adminCc = form.cc && form.cc.includes(admin.email)
  const adminBcc = form.bcc && form.bcc.includes(admin.email)
  const [showPlaceholderValues, setShowPlaceholderValues, toggleShowPlaceholderValues] = useBoolState(false)
  const [showDiscardConfirmation, setShowDiscardConfirmation, toggleShowDiscardConfirmation] = useBoolState()

  email = omitBy(email, isEmpty)
  useEffect(() => resetForm(email), [email])

  const fillPlaceholders = (content = '') => {
    return Object.keys(placeholders).reduce((result, placeholder) => {
      return result.replace(`[${placeholder}]`, placeholders[placeholder])
    }, content)
  }

  const handleTogglePlaceholders = e => {
    e.preventDefault()
    toggleShowPlaceholderValues()
  }

  const handleAddAdminCc = e => {
    e.preventDefault()
    let value = [form.cc, admin.email].filter(t => t).join(', ')
    setForm({cc: value})
  }

  const handleRemoveAdminCc = e => {
    e.preventDefault()
    let value = form.cc.replace(new RegExp(',? *' + admin.email), '')
    setForm({cc: value})
  }

  const handleAddAdminBcc = e => {
    e.preventDefault()
    let value = [form.bcc, admin.email].filter(t => t).join(', ')
    setForm({bcc: value})
  }

  const handleRemoveAdminBcc = e => {
    e.preventDefault()
    let value = form.bcc.replace(new RegExp(',? *' + admin.email), '')
    setForm({bcc: value})
  }

  const handleExit = e => {
    e.preventDefault()
    onExit && onExit(form)
    resetForm(email)
    setShowDiscardConfirmation(false)
  }

  const handleSend = e => {
    e.preventDefault()
    onSend(form)
  }

  const handleSave = e => {
    e.preventDefault()
    onSave(form)
  }

  const handleDiscard = diff(form, email) ? toggleShowDiscardConfirmation : handleExit

  return (
    <Fragment>
      <Modal onClose={handleDiscard} flashId='edit-email'>
        <Header title='Edit email message' subtitle={children}>
          {onSend ? (
            <Fragment>
              <button className='button is-text is-decorationless' onClick={handleDiscard}>
                Cancel
              </button>
              <button className='button is-primary' onClick={handleSend}>
                {buttonText || 'Send'}
              </button>
            </Fragment>
          ) : onSave ? (
            <Fragment>
              <button className='button is-text is-decorationless' onClick={handleDiscard}>
                Cancel
              </button>
              <button className='button is-primary' onClick={handleSave}>
                {buttonText || 'Save'}
              </button>
            </Fragment>
          ) : null}
        </Header>
        <section className='section'>
          <form>
            <InputField
              type='text'
              disabled={true}
              name='to'
              errors={errors.to}
              value={form.to || ''}
              onChange={setForm}
            />
            <a href='' onClick={adminCc ? handleRemoveAdminCc : handleAddAdminCc} className='is-pulled-right is-size-7'>
              {adminCc ? "Don't cc" : 'Cc'} me
            </a>
            <InputField
              type='text'
              name='cc'
              errors={errors.cc}
              value={form.cc || ''}
              onChange={setForm}
              help='Separate multiple email addresses with a comma (,).'
            />
            <a
              href=''
              onClick={adminBcc ? handleRemoveAdminBcc : handleAddAdminBcc}
              className='is-pulled-right is-size-7'>
              {adminBcc ? "Don't bcc" : 'Bcc'} me
            </a>
            <InputField type='text' name='bcc' errors={errors.bcc} value={form.bcc || ''} onChange={setForm} />
            {!isEmpty(placeholders) && (
              <a href='' onClick={handleTogglePlaceholders} className='is-pulled-right is-size-7'>
                Toggle placeholders
              </a>
            )}
            <InputField
              type='text'
              disabled={showPlaceholderValues}
              name='subject'
              errors={errors.subject}
              value={showPlaceholderValues ? fillPlaceholders(form.subject) : form.subject}
              onChange={showPlaceholderValues ? null : setForm}
            />
            <InputField
              type='textarea'
              disabled={showPlaceholderValues}
              name='message'
              rows={8}
              errors={errors.message}
              value={showPlaceholderValues ? fillPlaceholders(form.message) : form.message}
              onChange={showPlaceholderValues ? null : setForm}
              help='Placeholders are filled in when new invoices are created.'
            />
          </form>
        </section>
      </Modal>
      {showDiscardConfirmation && (
        <Confirmation
          confirm='Discard your changes?'
          onConfirm={handleExit}
          onCancel={toggleShowDiscardConfirmation}
          confirmText='Discard'
          cancelText='Continue editing'
          buttonClass='is-danger'
          minWidth={null}
        />
      )}
    </Fragment>
  )
}
